import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API, { fmt } from '@/lib/api';
import { Plus, Trash, Play, PencilSimple, Repeat, Pause, CheckCircle, Warning } from '@phosphor-icons/react';

const FREQ_LABELS = { wekelijks: 'week', maandelijks: 'maand', kwartaal: 'kwartaal', jaarlijks: 'jaar' };
const STATUS_BADGE = {
  actief: { bg: '#ECFDF5', color: '#059669', label: 'Actief', Icon: CheckCircle },
  gepauzeerd: { bg: '#FFFBEB', color: '#D97706', label: 'Gepauzeerd', Icon: Pause },
  gestopt: { bg: '#F3F4F6', color: '#6B7280', label: 'Gestopt', Icon: Warning },
};

export default function HerhaaldPage() {
  const [schemas, setSchemas] = useState([]);
  const [klanten, setKlanten] = useState([]);
  const [q, setQ] = useState('');
  const navigate = useNavigate();

  const load = () => {
    API.get('/schemas').then(r => setSchemas(r.data)).catch(() => {});
    API.get('/klanten').then(r => setKlanten(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const verwerk = async (id) => {
    if (!window.confirm('Schema nu verwerken en factuur aanmaken?')) return;
    try {
      await API.post(`/schemas/${id}/verwerk`);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Fout bij verwerken');
    }
  };

  const del = async (id) => {
    if (!window.confirm('Schema verwijderen?')) return;
    await API.delete(`/schemas/${id}`);
    load();
  };

  const kNaam = (kid) => {
    const k = klanten.find(x => x.id === kid);
    return k ? (k.bedrijf || `${k.voorn} ${k.achtern}`) : kid || '-';
  };

  const freqLabel = (s) => {
    const interval = parseInt(s.interval_aantal) || 1;
    const base = FREQ_LABELS[s.freq] || s.freq;
    return interval === 1 ? `Elke ${base}` : `Elke ${interval} ${base}${base.endsWith('l') ? 'len' : 'en'}`;
  };

  const eindeLabel = (s) => {
    if (s.aantal_max) {
      const done = parseInt(s.aantal_gedaan) || 0;
      return `${done}/${s.aantal_max} verwerkt`;
    }
    if (s.eind_datum) return `Tot ${s.eind_datum}`;
    return 'Onbeperkt';
  };

  const filtered = schemas.filter(s => !q || (s.naam || '').toLowerCase().includes(q.toLowerCase()) || kNaam(s.klant_id).toLowerCase().includes(q.toLowerCase()));

  // KPI
  const totActief = schemas.filter(s => s.status === 'actief').length;
  const mrr = schemas.filter(s => s.status === 'actief').reduce((sum, s) => {
    const v = parseFloat(s.bedrag) || 0;
    const mult = s.freq === 'wekelijks' ? 4.33 : s.freq === 'maandelijks' ? 1 : s.freq === 'kwartaal' ? 1/3 : 1/12;
    return sum + v * mult / (parseInt(s.interval_aantal) || 1);
  }, 0);

  return (
    <div data-testid="herhaald-page" className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-bold text-[#002FA7] flex items-center gap-2">
          <Repeat size={28} weight="bold" /> Herhaalde facturen
        </h1>
        <button className="btn btn-primary inline-flex items-center gap-1.5" onClick={() => navigate('/schemas/nieuw')} data-testid="new-schema-btn">
          <Plus size={14} weight="bold" /> Nieuw schema
        </button>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="section-card p-4" style={{ borderLeft: '4px solid #059669' }}>
          <div className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Actieve schema's</div>
          <div className="text-2xl font-black text-[#059669] mt-1">{totActief}</div>
        </div>
        <div className="section-card p-4" style={{ borderLeft: '4px solid #002FA7' }}>
          <div className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Geschatte MRR</div>
          <div className="text-2xl font-black text-[#002FA7] mt-1">{fmt(mrr)}</div>
        </div>
        <div className="section-card p-4" style={{ borderLeft: '4px solid #D97706' }}>
          <div className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Totaal schema's</div>
          <div className="text-2xl font-black text-[#D97706] mt-1">{schemas.length}</div>
        </div>
      </div>

      <div>
        <input className="field-input" style={{ maxWidth: 300 }} placeholder="Zoeken op naam of klant..." value={q} onChange={e => setQ(e.target.value)} data-testid="schema-search" />
      </div>

      <div className="section-card">
        <div className="section-body">
          <table className="data-table">
            <thead>
              <tr>
                <th>Naam</th>
                <th>Klant</th>
                <th>Frequentie</th>
                <th className="right">Bedrag</th>
                <th>Volgende</th>
                <th>Einde</th>
                <th>Status</th>
                <th>Acties</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => {
                const stInfo = STATUS_BADGE[s.status] || STATUS_BADGE.gestopt;
                const StIcon = stInfo.Icon;
                return (
                  <tr key={s.id} className="cursor-pointer hover:bg-gray-50" onClick={() => navigate(`/schemas/${s.id}/bewerk`)}>
                    <td className="font-medium">
                      {s.naam}
                      {s.auto_versturen && <span className="ml-2 text-[9px] bg-blue-50 text-blue-700 px-1.5 py-0.5 font-bold uppercase tracking-wider">Auto-mail</span>}
                    </td>
                    <td>{kNaam(s.klant_id)}</td>
                    <td>{freqLabel(s)}</td>
                    <td className="right font-medium">{fmt(s.bedrag)}</td>
                    <td>{s.volgende || '-'}</td>
                    <td className="text-xs text-gray-500">{eindeLabel(s)}</td>
                    <td>
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5" style={{ background: stInfo.bg, color: stInfo.color }}>
                        <StIcon size={10} weight="bold" /> {stInfo.label}
                      </span>
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <div className="flex gap-1 items-center">
                        <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/schemas/${s.id}/bewerk`)} data-testid={`edit-schema-${s.id}`} title="Bewerken">
                          <PencilSimple size={14} />
                        </button>
                        {s.status === 'actief' && (
                          <button className="btn btn-ghost btn-sm" onClick={() => verwerk(s.id)} data-testid={`verwerk-schema-${s.id}`} title="Nu verwerken">
                            <Play size={14} weight="fill" className="text-[#059669]" />
                          </button>
                        )}
                        <button className="btn btn-danger btn-sm" onClick={() => del(s.id)} data-testid={`del-schema-${s.id}`} title="Verwijderen">
                          <Trash size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan="8" className="text-center text-gray-400 py-12">
                    <Repeat size={32} weight="light" className="inline mb-2 text-gray-300" />
                    <div className="text-sm">Nog geen herhaalde facturen ingesteld</div>
                    <button className="btn btn-primary btn-sm mt-3 inline-flex items-center gap-1" onClick={() => navigate('/schemas/nieuw')}>
                      <Plus size={12} weight="bold" /> Maak je eerste schema
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
